import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/getAuth'
import '../../factor.css'
import { callAPI } from '../../hooks/api'
import SignInVerifyCode from './sign-in-verify-code'
import SingInInputEmail from './sign-in-input-email'
import SignInAddPassword from './sign-in-add-password'
import SingInInputPassword from './sign-in-input-password'
import { toast } from 'react-toastify'
import SignInUpdatePassword from './sign-in-update-password'
import {
  EFreeTrialStatusType,
  USER_FREE_TRAIL_STATUS_QUERY_KEY,
} from '@/components/welcome-modal/welcome-modal'
import { useQueryClient } from '@tanstack/react-query'

enum SignInSteps {
  InputEmail = 'input-email',
  VerifyCode = 'verify-code',
  AddPassword = 'add-password',
  InputPassword = 'input-password',
  UpdatePassword = 'update-password',
}

export enum EUserCredentialStatus {
  Fail = 'fail',
  Success = 'success',
  AddPassword = 'add-password',
  Update = 'update',
  NoSub = 'no sub',
}

export default function SignInPage({
  freshRestart,
  currentTab,
  setCurrentTab,
  setJoyrideRun,
  setFirstLogin,
}: {
  freshRestart: any
  currentTab: any
  setCurrentTab: any
  setJoyrideRun: any
  setFirstLogin: any
}) {
  const removeStylesheet = (href: any) => {
    const link = document.querySelector(`link[href="${href}"]`)
    if (link) {
      document.body.removeChild(link)
    }
  }
  removeStylesheet('/css/landing.css')
  removeStylesheet('/css/bootstrap.css')
  const queryClient = useQueryClient()
  const { setAuth, isLoggedIn, setIsLoggedIn } = useAuth()
  const [email, setEmail] = useState<string>('')
  const [oldPassword, setOldPassword] = useState<string>('')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<SignInSteps>(
    SignInSteps.InputEmail
  )
  const localAuth = localStorage.getItem('auth')
    ? JSON.parse(localStorage.getItem('auth') || '{}')
    : {}
  const isDemoUser =
    localAuth.sessionKey === 'a6cf9f6e-3341-4996-8d6a-83e826b67adf'

  useEffect(() => {
    if (isDemoUser) {
      localStorage.setItem('auth', JSON.stringify({}))
      setAuth(null)
      setIsLoggedIn(false)
      navigate('/')
    }
  }, [isDemoUser])

  useEffect(() => {
    const auth = localStorage.getItem('auth')
    if (isLoggedIn && auth) {
      const { sessionKey } = JSON.parse(auth)
      if (sessionKey) {
        callAPI('/get_is_subscribed', {
          session_key: sessionKey,
        }).then((res) => {
          if (!res.is_subscribed) {
            navigate(
              searchParams.get('locations')
                ? `/subscribe?locations=${searchParams.get('locations')}`
                : '/subscribe',
              { replace: true }
            )
            return
          }

          navigate(
            searchParams.get('locations')
              ? `/?locations=${searchParams.get('locations')}`
              : '/',
            { replace: true }
          )
        })
      }
    }
  }, [isLoggedIn])

  useEffect(() => {
    freshRestart()
    queryClient.invalidateQueries({})
    setCurrentTab('sign-in')
  }, [])

  async function handleUserCredential({
    userCredential,
    oldPassword,
  }: {
    userCredential: any
    oldPassword?: string
  }) {
    if (userCredential.status === EUserCredentialStatus.Fail) {
      return toast.warn(userCredential.msg)
    }

    if (userCredential.status === EUserCredentialStatus.Update) {
      toast.warn('Please update your password!')
      if (!oldPassword) {
        throw new Error('Old password is required')
      }
      setOldPassword(oldPassword)
      setCurrentStep(SignInSteps.UpdatePassword)
    }

    if (
      userCredential.status === EUserCredentialStatus.NoSub ||
      userCredential.status === EUserCredentialStatus.Success
    ) {
      localStorage.setItem('auth', JSON.stringify(userCredential))
      setAuth(userCredential)
      let redirectUrl = searchParams.get('locations')
        ? `/${userCredential.status === 'no sub' ? 'subscribe' : ''}?locations=${searchParams.get('locations')}`
        : userCredential.status === 'no sub'
          ? '/subscribe'
          : '/'
      const trialStatus = userCredential.trial_status
      if (trialStatus) {
        if (
          [
            EFreeTrialStatusType.NEW_TRIAL,
            EFreeTrialStatusType.START_TRIAL,
            EFreeTrialStatusType.ACTIVE_TRIAL,
          ].includes(trialStatus)
        ) {
          redirectUrl += `?${USER_FREE_TRAIL_STATUS_QUERY_KEY}=${trialStatus}`
        }
      }

      if (userCredential.status === EUserCredentialStatus.Success) {
        setJoyrideRun(userCredential.tutorial)
        setFirstLogin(userCredential.firstLogin)
        setIsLoggedIn(true)
        navigate(redirectUrl, { replace: true })
      } else {
        navigate(redirectUrl)
      }
    }
  }

  const handleResetStep = () => {
    setCurrentStep(SignInSteps.InputEmail)
    setEmail('')
  }

  if (currentStep === SignInSteps.InputEmail) {
    return (
      <SingInInputEmail
        onHasAccount={(email: string) => {
          setEmail(email)
          setCurrentStep(SignInSteps.InputPassword)
        }}
        onNoAccount={(email: string) => {
          setEmail(email)
          setCurrentStep(SignInSteps.VerifyCode)
        }}
        onLoginSocialSuccess={(data: any) => {
          handleUserCredential({
            userCredential: data,
          })
        }}
      />
    )
  }

  if (currentStep === SignInSteps.VerifyCode) {
    return (
      <SignInVerifyCode
        email={email}
        onClickBack={handleResetStep}
        onVerifySuccess={() => {
          return setCurrentStep(SignInSteps.AddPassword)
        }}
      />
    )
  }

  if (currentStep === SignInSteps.AddPassword) {
    return (
      <SignInAddPassword
        email={email}
        onClickBack={handleResetStep}
        onSuccess={(data) => {
          handleUserCredential({
            userCredential: data.userCredential,
            oldPassword: data.oldPassword,
          })
        }}
      />
    )
  }

  if (currentStep === SignInSteps.InputPassword) {
    return (
      <SingInInputPassword
        email={email}
        onClickBack={handleResetStep}
        onSuccess={(data) => {
          handleUserCredential({
            userCredential: data.userCredential,
            oldPassword: data.oldPassword,
          })
        }}
      />
    )
  }
  if (currentStep === SignInSteps.UpdatePassword) {
    return (
      <SignInUpdatePassword
        email={email}
        onClickBack={handleResetStep}
        onSuccess={(data) => {
          handleUserCredential({
            userCredential: data,
            oldPassword: oldPassword,
          })
        }}
        oldPassword={oldPassword}
      />
    )
  }
}
